import { ReactNode } from 'react';
import { AppStoreCallout } from './AppStoreCallout';
import { SectionGlow } from './SectionGlow';
import mockupSummary from '@/assets/mockup-summary.png';
import mockupFeed from '@/assets/mockup-feed.png';
import mockupPlayer from '@/assets/mockup-player.png';
import mockupAskAi from '@/assets/mockup-ask-ai.png';
import mockupLibrary from '@/assets/mockup-library.png';

type GlowPos = 'top' | 'bottom' | 'center' | 'right' | 'top-left' | 'bottom-right';

interface FeatureSectionProps {
  headlineLead: string;
  headlineItalic: string;
  headlineTrail?: string;
  body: ReactNode;
  image: string;
  imageAlt: string;
  reverse?: boolean;
  glow: GlowPos;
  warm?: boolean;
  extra?: ReactNode;
  narrowImage?: boolean;
}

function FeatureSection({
  headlineLead,
  headlineItalic,
  headlineTrail,
  body,
  image,
  imageAlt,
  reverse,
  glow,
  warm,
  extra,
  narrowImage = true,
}: FeatureSectionProps) {
  return (
    <section
      className="relative overflow-hidden py-20 md:py-28"
      style={{ backgroundColor: warm ? '#0c0a08' : '#070707' }}
    >
      <SectionGlow position={glow} />
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Copy */}
          <div className={reverse ? 'md:order-2' : 'md:order-1'}>
            <h2
              className="font-display font-normal text-text mb-6"
              style={{
                fontSize: 'clamp(2.5rem, 9vw, 4.25rem)',
                lineHeight: 0.95,
                letterSpacing: '-0.022em',
              }}
            >
              {headlineLead}{' '}
              <em
                className="not-italic"
                style={{ fontStyle: 'italic', color: '#e0b352' }}
              >
                {headlineItalic}
              </em>
              {headlineTrail ? <> {headlineTrail}</> : null}
            </h2>
            <div
              className="font-body text-text/[0.62] max-w-xl space-y-4"
              style={{
                fontSize: 'clamp(1.0625rem, 1.5vw, 1.25rem)',
                lineHeight: 1.5,
                letterSpacing: '-0.005em',
              }}
            >
              {body}
            </div>
            {extra ? <div className="mt-6">{extra}</div> : null}
          </div>

          {/* iPhone mockup */}
          <div className={reverse ? 'md:order-1' : 'md:order-2'}>
            <div
              className={`relative mx-auto w-full ${
                narrowImage
                  ? 'max-w-[240px] sm:max-w-[300px] md:max-w-[340px]'
                  : 'max-w-[340px]'
              }`}
            >
              <img
                src={image}
                alt={imageAlt}
                className="w-full h-auto object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Italian',
  'Portuguese', 'Russian', 'Hindi', 'Japanese',
  'Korean', 'Mandarin Chinese', 'Arabic',
];

export function Features() {
  return (
    <>
      <FeatureSection
        glow="bottom"
        headlineLead="Save"
        headlineItalic="Hours"
        headlineTrail="Every Month."
        image={mockupSummary}
        imageAlt="PodBrief summary showing 2 hours and 30 minutes saved"
        body={
          <>
            <p>
              Our average user saves at least{' '}
              <span className="text-text font-bold">10 hours a month</span>{' '}
              with AI-generated summaries. For example, the 2 hour 42 minute
              episode here becomes a 5-minute read or 12 minute listen — same
              key ideas, none of the filler.
            </p>
            <p>
              Reclaim the time you lose from long-form audio without losing the
              context.
            </p>
          </>
        }
      />

      <FeatureSection
        warm
        reverse
        glow="center"
        headlineLead="Your"
        headlineItalic="Personal"
        headlineTrail="Media Feed"
        image={mockupFeed}
        imageAlt="Personalized podcast feed with followed shows"
        body={
          <>
            <p>
              Follow the podcasts and YouTube channels you already love and the
              ones you've been meaning to try. PodBrief pulls every new episode
              into <span className="text-text font-bold">one clean feed</span>{' '}
              and lets you summarize anything in a single tap.
            </p>

            <p>
              No juggling apps, no losing track of what dropped — your whole
              listening world in one place.
            </p>
          </>
        }
      />

      <FeatureSection
        glow="right"
        headlineLead="Listen Just"
        headlineItalic="Like"
        headlineTrail="A Playlist"
        image={mockupPlayer}
        imageAlt="PodBrief player with podcast episode playing"
        body={
          <>
            <p>
              Every summary is narrated by a natural AI voice and{' '}
              <span className="text-text font-bold">queued back-to-back</span>,
              just like a playlist. Hit play once and move through five shows on
              your commute, at the gym, or while you cook.
            </p>
            <p>
              Adjust speed, skip ahead, or open the full episode whenever
              something hooks you.
            </p>
          </>
        }
      />

      <FeatureSection
        warm
        reverse
        glow="top-left"
        headlineLead="Dive"
        headlineItalic="Deeper"
        headlineTrail="Into Your Content"
        image={mockupAskAi}
        imageAlt="Ask AI chat answering questions about a podcast episode"
        body={
          <>
            <p>
              Ask our AI anything about any episode and get a{' '}
              <span className="text-text font-bold">grounded answer in seconds</span>.
              Want the guest's strongest argument, the moment they mentioned a
              specific company, or more information about something they
              mentioned?
            </p>
            <p>
              It's all there — just a few clicks away.
            </p>
          </>
        }
      />

      <FeatureSection
        narrowImage={false}
        glow="bottom-right"
        headlineLead="Built for"
        headlineItalic="Global"
        headlineTrail="Listeners"
        image={mockupLibrary}
        imageAlt="Library of summaries across multiple languages"
        body={
          <>
            <p>
              Summarize an English podcast into Spanish, a French interview into
              Japanese, a German show into Hindi. PodBrief supports{' '}
              <span className="text-text font-bold">13 languages end-to-end</span>{' '}
              so you can follow the best conversations in the world in the
              language you actually think in.
            </p>
          </>
        }
        extra={
          <div className="flex flex-wrap gap-2 max-w-xl">
            {LANGUAGES.map((lang) => (
              <span
                key={lang}
                className="px-3 py-1 rounded-full border border-text/10 bg-text/[0.03] text-text/[0.7] text-sm font-body"
              >
                {lang}
              </span>
            ))}
          </div>
        }
      />

      <AppStoreCallout />
    </>
  );
}
